import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Home,
  Users,
  FileText,
  ClipboardList,
  Receipt,
  Download,
  LogOut,
  Menu,
  X,
  User,
  KeyRound,
  Settings as SettingsIcon,
  Inbox,
  Mail,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI, companyAPI } from '../../services/api';
import ChangePasswordModal from '../Common/ChangePasswordModal';
import './Layout.css';

const Layout = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [company, setCompany] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    companyAPI.get()
      .then(({ data }) => setCompany(data))
      .catch(() => {});
  }, []);

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home
    },
    {
      name: 'Kunden',
      href: '/customers',
      icon: Users
    },
    {
      name: 'Angebote',
      href: '/offers',
      icon: ClipboardList
    },
    {
      name: 'Rechnungen',
      href: '/invoices',
      icon: FileText
    },
    {
      name: 'Eingangsrechnungen',
      href: '/incoming',
      icon: Inbox
    },
    {
      name: 'Ausgaben',
      href: '/expenses',
      icon: Receipt
    },
    {
      name: 'DATEV Export',
      href: '/datev',
      icon: Download
    },
    {
      name: 'E-Mail',
      href: '/email',
      icon: Mail
    },
    {
      name: 'Einstellungen',
      href: '/settings',
      icon: SettingsIcon
    }
  ];

  const isCurrentPath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      onLogout();
      toast.success('Erfolgreich abgemeldet');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      onLogout();
      navigate('/login');
    }
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="app-container">
      {/* Mobile menu button */}
      <div className="mobile-menu-button">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="btn btn-outline"
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          {company?.logo_url && (
            <img
              src={companyAPI.logoUrl()}
              alt={company.name || 'Logo'}
              className="sidebar-logo"
            />
          )}
          <h2>{company?.name || 'Schiffer Digital'}</h2>
          <p>Business Center</p>
        </div>

        <nav className="sidebar-nav">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = isCurrentPath(item.href);

            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={closeSidebar}
                className={`nav-item ${isActive ? 'nav-item-active' : ''}`}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              <User size={20} />
            </div>
            <div className="user-details">
              <p className="user-email">{user?.email}</p>
              <p className="user-role">Administrator</p>
            </div>
          </div>

          <button
            onClick={() => setShowPasswordModal(true)}
            className="logout-btn"
            title="Passwort ändern"
          >
            <KeyRound size={18} />
            <span>Passwort ändern</span>
          </button>

          <button
            onClick={handleLogout}
            className="logout-btn"
            title="Abmelden"
          >
            <LogOut size={18} />
            <span>Abmelden</span>
          </button>
        </div>
      </div>

      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}

      {/* Main content */}
      <div className="main-content">
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div
            className="mobile-backdrop"
            onClick={closeSidebar}
          />
        )}

        <Outlet />
      </div>
    </div>
  );
};

export default Layout;